import { useEffect, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, User, Tag, AlertCircle, CalendarPlus } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import FilterBar from '@/components/FilterBar';
import AIAnalysisPanel from '@/components/AIAnalysisPanel';
import IssueIdLink from '@/components/IssueIdLink';
import AddToCalendarModal from '@/components/AddToCalendarModal';
import CalendarIndicator from '@/components/CalendarIndicator';
import useCalendarizedIssues from '@/hooks/useCalendarizedIssues';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function KanbanBoard({ token }) {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [filters, setFilters] = useState({ project: '', category: '', user: '', status: '', includeResolved: false });
  const [calendarModalIssue, setCalendarModalIssue] = useState(null);
  
  // Calendar tracking
  const { getCalendarEvents, refresh: refreshCalendar } = useCalendarizedIssues(token);

  useEffect(() => {
    fetchIssues();
  }, [token]);

  useEffect(() => {
    if (!loading) {
      fetchIssues();
    }
  }, [filters]);

  const fetchIssues = async () => {
    try {
      const params = new URLSearchParams({ page_size: 1000 });
      
      // Add includeResolved flag (default is false = stato < 80)
      if (filters.includeResolved) {
        params.append('include_resolved', 'true');
      }
      
      if (filters.status && filters.status !== 'all') {
        params.append('filter_state', filters.status);
      }
      if (filters.project && filters.project !== 'all') {
        params.append('filter_project', filters.project);
      }
      if (filters.category && filters.category !== 'all') {
        params.append('filter_category', filters.category);
      }
      if (filters.user && filters.user !== 'all') {
        params.append('filter_user', filters.user);
      }

      const response = await axios.get(`${BACKEND_URL}/api/issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      
      setIssues(response.data.issues);
      setFilteredIssues(response.data.issues);
      
      const statuses = [...new Set((response.data.issues || []).map((i) => i.status?.name).filter(Boolean))];
      setStatusOptions(statuses);
    } catch (error) {
      toast.error('Errore nel caricamento delle issue');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchIssues();
      toast.success('Dati aggiornati con successo!');
    } catch (error) {
      toast.error(`Errore nell'aggiornamento: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const groupedIssues = statusOptions.reduce((acc, status) => {
    acc[status] = filteredIssues.filter((issue) => issue.status?.name === status);
    return acc;
  }, {});

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;
    toast.info('Drag & drop non ancora implementato per aggiornare lo stato su MantisBT');
  };

  const handleIssueClick = (issue) => {
    setSelectedIssue(issue);
    setDialogOpen(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedIssue) return;
    try {
      await axios.patch(
        `${BACKEND_URL}/api/issues/${selectedIssue.id}/status`,
        { status: { name: newStatus } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status aggiornato con successo!');
      fetchIssues();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Errore nell'aggiornamento dello stato");
      console.error(error);
    }
  };

  const getPriorityColor = (priority) => {
    const p = priority?.toLowerCase() || '';
    if (p.includes('urgent') || p.includes('immediate')) return 'rgb(239, 68, 68)';
    if (p.includes('high')) return 'rgb(245, 158, 11)';
    if (p.includes('normal')) return 'rgb(59, 130, 246)';
    return 'rgb(161, 161, 170)';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
      </div>
    );
  }

  const projects = [...new Set((issues || []).map(i => i.project?.name).filter(Boolean))];
  const categories = [...new Set((issues || []).map(i => i.category?.name).filter(Boolean))];
  const users = [...new Set((issues || []).map(i => i.handler?.real_name || i.handler?.name).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="kanban-board">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-mono mb-2" data-testid="kanban-title">
          Kanban Board
        </h1>
        <p className="text-[rgb(161,161,170)] text-sm font-mono uppercase tracking-widest">
          Visual task management
        </p>
      </div>

      <FilterBar
        projects={projects}
        categories={categories}
        users={users}
        statuses={statusOptions}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        onAIAnalysis={() => setAiPanelOpen(true)}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 300px)' }}>
          {statusOptions.map((status) => (
            <Droppable droppableId={status} key={status}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="min-w-[300px] bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 rounded-md backdrop-blur-sm flex flex-col"
                  data-testid={`kanban-column-${status}`}
                >
                  <div className="p-4 border-b border-[rgb(39,39,42)]/50">
                    <h3 className="font-mono font-bold text-sm uppercase tracking-widest">{status}</h3>
                    <p className="text-[rgb(161,161,170)] text-xs font-mono mt-1">
                      {groupedIssues[status]?.length || 0} issues
                    </p>
                  </div>
                  <div className="p-4 space-y-3 flex-1 overflow-y-auto">
                    {groupedIssues[status]?.map((issue, index) => (
                      <Draggable key={issue.id} draggableId={issue.id.toString()} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            onClick={() => handleIssueClick(issue)}
                            className="bg-[rgb(39,39,42)]/30 border border-[rgb(39,39,42)] p-3 rounded-sm cursor-pointer hover:border-[rgb(59,130,246)]/50 hover:bg-[rgb(39,39,42)]/50 transition-all duration-200"
                            data-testid={`kanban-issue-${issue.id}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-1.5">
                                <IssueIdLink issueId={issue.id} className="text-xs font-mono" />
                                <CalendarIndicator events={getCalendarEvents(issue.id)} />
                              </div>
                              <Badge
                                className="rounded-sm px-2 py-0.5 text-xs font-mono uppercase tracking-wider"
                                style={{ backgroundColor: getPriorityColor(issue.priority?.label) }}
                              >
                                {issue.priority?.label || 'N/A'}
                              </Badge>
                            </div>
                            <h4 className="text-sm font-medium mb-2 line-clamp-2">{issue.summary}</h4>
                            <div className="flex items-center gap-2 text-xs text-[rgb(161,161,170)]">
                              <User className="h-3 w-3" />
                              <span className="truncate">{issue.handler?.real_name || issue.handler?.name || 'Unassigned'}</span>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Issue Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="issue-detail-dialog">
          {selectedIssue && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-2xl flex items-center gap-2" data-testid="issue-detail-title">
                  <IssueIdLink issueId={selectedIssue.id} />
                  <span>: {selectedIssue.summary}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                      Status
                    </span>
                    <Select defaultValue={selectedIssue.status?.name} onValueChange={handleStatusChange}>
                      <SelectTrigger className="bg-black/20 border-[rgb(39,39,42)] font-mono" data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[rgb(24,24,27)] border-[rgb(39,39,42)]">
                        {statusOptions.map((status) => (
                          <SelectItem key={status} value={status} className="font-mono">
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                      Priority
                    </span>
                    <Badge
                      className="rounded-sm px-3 py-1 text-sm font-mono uppercase tracking-wider"
                      style={{ backgroundColor: getPriorityColor(selectedIssue.priority?.label) }}
                    >
                      {selectedIssue.priority?.label || 'N/A'}
                    </Badge>
                  </div>
                </div>

                <div>
                  <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                    Assegnato a
                  </span>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4" />
                    <span>{selectedIssue.handler?.real_name || selectedIssue.handler?.name || 'Unassigned'}</span>
                  </div>
                </div>

                <div>
                  <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                    Progetto
                  </span>
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4" />
                    <span>{selectedIssue.project?.name || 'N/A'}</span>
                  </div>
                </div>

                <div>
                  <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                    Categoria
                  </span>
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4" />
                    <span>{selectedIssue.category?.name || 'N/A'}</span>
                  </div>
                </div>

                {selectedIssue.due_date && (
                  <div>
                    <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                      Scadenza
                    </span>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4" />
                      <span>{format(new Date(selectedIssue.due_date), 'PPP', { locale: it })}</span>
                    </div>
                  </div>
                )}

                <div>
                  <span className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono block mb-2">
                    Descrizione
                  </span>
                  <div className="bg-black/20 border border-[rgb(39,39,42)] p-4 rounded-md text-sm whitespace-pre-wrap">
                    {selectedIssue.description || 'Nessuna descrizione'}
                  </div>
                </div>
                
                {/* Add to Calendar Button */}
                <div className="pt-4 border-t border-[rgb(39,39,42)]">
                  <Button
                    onClick={() => {
                      setDialogOpen(false);
                      setCalendarModalIssue(selectedIssue);
                    }}
                    variant="outline"
                    className="w-full font-mono border-[rgb(59,130,246)]/50 text-[rgb(59,130,246)] hover:bg-[rgb(59,130,246)]/10"
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Aggiungi al Calendario
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Calendar Modal */}
      {calendarModalIssue && (
        <AddToCalendarModal
          issue={calendarModalIssue}
          token={token}
          onClose={() => setCalendarModalIssue(null)}
          onSuccess={() => {
            toast.success('Evento aggiunto al calendario!');
            refreshCalendar();
          }}
        />
      )}

      <AIAnalysisPanel
        issues={filteredIssues}
        context="Kanban Board - Analisi del workflow e distribuzione delle issue per stato"
        token={token}
        isOpen={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
      />
    </div>
  );
}

export default KanbanBoard;
